import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, User, Package, Phone, Mail, RotateCcw, MessageSquare, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { flagForRecord } from '@/lib/countryFlags';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (amount) => {
  if (typeof amount !== 'number') return '—';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatusBadge = ({ status, tone = 'slate' }) => {
  if (!status) return null;
  const tones = {
    slate: 'bg-slate-700/40 text-slate-200 border-slate-600/40',
    green: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    rose: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    amber: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    blue: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  };
  return (
    <Badge className={`${tones[tone] || tones.slate} text-xs capitalize`}>
      {String(status).replace(/_/g, ' ')}
    </Badge>
  );
};

const courierTone = (status) => {
  if (!status) return 'slate';
  if (status.startsWith('rto_')) return 'rose';
  if (status === 'delivered') return 'green';
  if (status === 'failed') return 'rose';
  if (status === 'out_for_delivery' || status === 'in_transit') return 'blue';
  return 'amber';
};

export default function Customer360Page() {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const search = async () => {
    if (!phone.trim() && !email.trim()) {
      toast.error('Enter a phone or email to search.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (phone.trim()) params.append('phone', phone.trim());
      if (email.trim()) params.append('email', email.trim().toLowerCase());
      const r = await axios.get(`${API_BASE_URL}/customers/by-contact?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
      const total = (r.data?.orders_count || 0) + (r.data?.leads_count || 0)
                    + (r.data?.returns_count || 0) + (r.data?.conversations_count || 0);
      if (total === 0 && !r.data?.customer) {
        toast.info('No matches found across orders, leads, returns, or inbox.');
      } else {
        toast.success(`Found ${r.data.orders_count} orders · ${r.data.leads_count} leads · ${r.data.returns_count} returns · ${r.data.conversations_count} conversations.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="customer-360-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
          <User className="w-6 h-6" /> Customer 360
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Search by phone in ANY format — `+91 98765 43210`, `9876543210`, `(987) 654-3210`. We resolve to every order, lead, return, and inbox conversation tied to that contact.
        </p>
      </div>

      {/* Search */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-stretch md:items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-slate-400 mb-1 block">Phone</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="+91 98765 43210"
                className="bg-slate-950 border-slate-700"
                data-testid="customer-360-phone-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-slate-400 mb-1 block">Email</label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="customer@example.com"
                className="bg-slate-950 border-slate-700"
                data-testid="customer-360-email-input"
              />
            </div>
            <Button
              onClick={search}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              data-testid="customer-360-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              {loading ? 'Searching…' : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          {/* Match info + summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="customer-360-summary">
            <SummaryCard label="Orders" value={data.orders_count} icon={Package} />
            <SummaryCard label="Leads" value={data.leads_count} icon={User} />
            <SummaryCard label="Returns" value={data.returns_count} icon={RotateCcw} />
            <SummaryCard label="Conversations" value={data.conversations_count} icon={MessageSquare} />
            <SummaryCard label="Total Spent" value={formatINR(data.summary?.total_spent || 0)} icon={Package} highlight />
          </div>

          {/* Customer card */}
          {data.customer && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4" /> Customer record
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-slate-300">
                  <span><span className="text-slate-500">Name:</span> {data.customer.full_name || '—'}</span>
                  {data.customer.phone && (
                    <span className="flex items-center gap-1" data-testid="c360-customer-phone">
                      <Phone className="w-3 h-3" />
                      {(() => {
                        const flag = flagForRecord(data.customer, 'phone_country_code', 'phone');
                        return flag ? (
                          <span
                            className="leading-none"
                            title={data.customer.phone_country_code ? `+${data.customer.phone_country_code}` : ''}
                          >
                            {flag}
                          </span>
                        ) : null;
                      })()}
                      {data.customer.phone}
                    </span>
                  )}
                  {data.customer.email && (
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {data.customer.email}</span>
                  )}
                </div>
                <div className="flex gap-2 text-xs">
                  <Badge variant="outline" className="border-slate-700 text-slate-400">
                    Customer ID: {data.customer.id?.slice(0, 8)}…
                  </Badge>
                  {data.customer.shopify_customer_id && (
                    <Badge variant="outline" className="border-emerald-700 text-emerald-300">
                      Shopify-synced
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Orders */}
          {data.orders?.length > 0 && (
            <SectionList title={`Orders (${data.orders.length})`} icon={Package}>
              {data.orders.map((o) => (
                <ListRow
                  key={o.id}
                  onClick={() => navigate(`/orders?focus=${o.id}`)}
                  data-testid={`c360-order-${o.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-amber-300">{o.order_number}</div>
                    <div className="text-xs text-slate-500 truncate">{o.customer_name || '—'}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {o.courier_status && <StatusBadge status={o.courier_status} tone={courierTone(o.courier_status)} />}
                    {o.fulfillment_status && o.fulfillment_status !== o.courier_status && (
                      <StatusBadge status={o.fulfillment_status} />
                    )}
                    {o.awb_number && (
                      <span className="font-mono text-xs text-slate-400">AWB {o.awb_number}</span>
                    )}
                    <span className="text-sm font-semibold text-slate-200">
                      {formatINR(parseFloat(o.total_amount || o.total_price || 0))}
                    </span>
                  </div>
                </ListRow>
              ))}
            </SectionList>
          )}

          {/* Leads */}
          {data.leads?.length > 0 && (
            <SectionList title={`Leads (${data.leads.length})`} icon={User}>
              {data.leads.map((l) => {
                const leadFlag = flagForRecord(l, 'phone_country_code', 'phone');
                return (
                <ListRow key={l.id} onClick={() => navigate(`/leads?focus=${l.id}`)}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200">{l.customer_name || '—'}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      {leadFlag && <span title={l.phone_country_code ? `+${l.phone_country_code}` : ''}>{leadFlag}</span>}
                      <span>{l.phone || l.email}</span>
                      {l.follow_up_date && <span>· follow-up {new Date(l.follow_up_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {l.stage && <StatusBadge status={l.stage} tone="blue" />}
                    {l.status && <StatusBadge status={l.status} />}
                  </div>
                </ListRow>
              );
              })}
            </SectionList>
          )}

          {/* Returns */}
          {data.returns?.length > 0 && (
            <SectionList title={`Returns (${data.returns.length})`} icon={RotateCcw}>
              {data.returns.map((r) => (
                <ListRow key={r.id} onClick={() => navigate(`/orders/returns?focus=${r.id}`)}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-rose-300">{r.return_number}</div>
                    <div className="text-xs text-slate-500">
                      Order {r.order_number} {r.pickup_awb && `· AWB ${r.pickup_awb}`}
                    </div>
                  </div>
                  <StatusBadge status={r.status} tone={r.status === 'inwarded' ? 'green' : 'amber'} />
                </ListRow>
              ))}
            </SectionList>
          )}

          {/* Conversations */}
          {data.conversations?.length > 0 && (
            <SectionList title={`Inbox conversations (${data.conversations.length})`} icon={MessageSquare}>
              {data.conversations.map((c) => {
                const convFlag = flagForRecord(c, 'customer_phone_country_code', 'customer_phone');
                return (
                <ListRow key={c.id} onClick={() => navigate(`/inbox?conv=${c.id}`)}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 flex items-center gap-1">
                      {convFlag && (
                        <span title={c.customer_phone_country_code ? `+${c.customer_phone_country_code}` : ''}>
                          {convFlag}
                        </span>
                      )}
                      <span>{c.customer_name || c.customer_phone}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate max-w-md">
                      {c.last_message || '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={c.channel} tone="blue" />
                    {(c.unread_count || 0) > 0 && (
                      <Badge className="bg-rose-500 text-white text-xs">{c.unread_count} unread</Badge>
                    )}
                  </div>
                </ListRow>
              );
              })}
            </SectionList>
          )}

          {/* Empty state */}
          {!data.customer && data.orders_count === 0 && data.leads_count === 0
           && data.returns_count === 0 && data.conversations_count === 0 && (
            <Card className="bg-slate-900 border-slate-800 border-dashed">
              <CardContent className="pt-8 pb-8 text-center text-slate-500 text-sm flex flex-col items-center gap-3">
                <AlertCircle className="w-10 h-10 text-slate-600" />
                No customer record, orders, leads, returns, or inbox conversations match this contact.
                Try the 10-digit number alone (we strip `+`/spaces/dashes automatically).
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, icon: Icon, highlight }) => (
  <Card className={`bg-slate-900 border-slate-800 ${highlight ? 'border-amber-500/40' : ''}`}>
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${highlight ? 'text-amber-300' : 'text-slate-100'}`}>
        {value}
      </div>
    </CardContent>
  </Card>
);

const SectionList = ({ title, icon: Icon, children }) => (
  <Card className="bg-slate-900 border-slate-800">
    <CardHeader>
      <CardTitle className="text-base flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />} {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-1.5">{children}</CardContent>
  </Card>
);

const ListRow = ({ children, onClick, ...rest }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.(e)}
    className="flex items-center gap-4 px-3 py-2 rounded-md bg-slate-950/40 hover:bg-slate-800/50 transition-colors cursor-pointer group"
    {...rest}
  >
    {children}
    <ExternalLink className="w-3.5 h-3.5 text-slate-600 group-hover:text-amber-300 flex-shrink-0" />
  </div>
);
